import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  LoadingState,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  toast,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { fetchMe } from '../../../api/auth.js';
import { listBuildings } from '../../../api/buildings.js';
import { listOrgUsers } from '../../../api/org.js';
import {
  addReminderNote,
  completeReminder,
  createReminder,
  fetchReminder,
  listReminders,
  updateReminder,
  type ReminderItem,
  type ReminderNoteItem,
} from '../../../api/reminders.js';
import { fetchUploadSignedUrl } from '../../../api/uploads.js';
import { VoiceRecorder } from '../../../components/voice-recorder.js';
import { ApiError } from '../../../lib/api-client.js';

const LOCATION_AUTO_SELECT_THRESHOLD = 0.55;

interface TasksListPageProps {
  /** When set, scopes the list to a building and defaults new tasks to it. */
  buildingId?: string;
}

export function RemindersListPage({ buildingId }: TasksListPageProps = {}) {
  return <TasksListPage buildingId={buildingId} />;
}

export function TasksListPage({ buildingId }: TasksListPageProps = {}) {
  const queryClient = useQueryClient();
  const meQuery = useQuery({ queryKey: ['me'], queryFn: fetchMe });
  const [query, setQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['reminders', { mine: false, q: query, buildingId }],
    queryFn: () =>
      listReminders({
        q: query,
        limit: 100,
        ...(buildingId
          ? { entityType: 'building', entityId: buildingId }
          : {}),
      }),
  });

  const completeMutation = useMutation({
    mutationFn: (id: string) => completeReminder(id, { done: true }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['reminders'] });
      void queryClient.invalidateQueries({ queryKey: ['reminders-summary'] });
      setSelectedId(null);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not complete task');
    },
  });

  const reminders = useMemo(() => listQuery.data?.reminders ?? [], [listQuery.data?.reminders]);
  const role = meQuery.data?.user.role;
  const canMutate = role === 'admin' || role === 'building_manager' || role === 'maintenance';

  if (listQuery.isLoading) return <LoadingState rows={5} />;
  if (listQuery.isError) {
    return (
      <Card>
        <CardContent className="p-4 text-sm text-danger">
          Could not load tasks right now.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle className="text-base">
            {buildingId ? 'Building tasks' : 'Tasks'}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search tasks"
              className="h-8 w-56"
            />
            {canMutate ? (
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                New task
              </Button>
            ) : null}
          </div>
        </CardHeader>
        <CardContent>
          {reminders.length === 0 ? (
            <p className="text-sm text-fg-muted">
              No tasks yet. Create one to track follow-ups with dated memos.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {reminders.map((reminder) => (
                <TaskRow
                  key={reminder.id}
                  reminder={reminder}
                  selected={selectedId === reminder.id}
                  onSelect={() => setSelectedId(reminder.id)}
                  onComplete={() => completeMutation.mutate(reminder.id)}
                  busy={completeMutation.isPending || !canMutate}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {selectedId ? (
        <TaskDetailPanel
          reminderId={selectedId}
          canMutate={canMutate}
          onClose={() => setSelectedId(null)}
        />
      ) : null}

      <CreateTaskSheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        buildingId={buildingId}
        onCreated={(id) => {
          setCreateOpen(false);
          setSelectedId(id);
          void queryClient.invalidateQueries({ queryKey: ['reminders'] });
        }}
      />
    </div>
  );
}

function TaskRow({
  reminder,
  selected,
  onSelect,
  onComplete,
  busy,
}: {
  reminder: ReminderItem;
  selected: boolean;
  onSelect: () => void;
  onComplete: () => void;
  busy: boolean;
}) {
  return (
    <li
      className={`flex items-center justify-between gap-3 py-3 ${selected ? 'bg-surface-muted/60' : ''}`}
    >
      <button type="button" className="min-w-0 flex-1 text-left" onClick={onSelect}>
        <div className="truncate text-sm font-medium">{reminder.title}</div>
        <div className="text-xs text-fg-muted">
          Due {new Date(reminder.dueAt).toLocaleString()} · {reminder.priority}
          {reminder.assignees.length > 0
            ? ` · ${reminder.assignees.map((a) => a.name ?? a.email).join(', ')}`
            : ''}
          {reminder.entityType === 'building' ? (
            <>
              {' '}
              ·{' '}
              <Link
                className="text-primary hover:underline"
                to={`/buildings/${reminder.entityId}`}
                onClick={(e) => e.stopPropagation()}
              >
                Building
              </Link>
            </>
          ) : null}
          {reminder.entityType === 'work_order' ? (
            <>
              {' '}
              ·{' '}
              <Link
                className="text-primary hover:underline"
                to={`/work-orders/${reminder.entityId}`}
                onClick={(e) => e.stopPropagation()}
              >
                Work order
              </Link>
            </>
          ) : null}
        </div>
        {reminder.description ? (
          <p className="mt-1 line-clamp-2 text-xs text-fg-secondary">{reminder.description}</p>
        ) : null}
      </button>
      <Button
        size="sm"
        variant="outline"
        onClick={onComplete}
        disabled={busy || reminder.status !== 'open'}
      >
        Done
      </Button>
    </li>
  );
}

function TaskDetailPanel({
  reminderId,
  canMutate,
  onClose,
}: {
  reminderId: string;
  canMutate: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [noteBody, setNoteBody] = useState('');
  const detailQuery = useQuery({
    queryKey: ['reminder', reminderId],
    queryFn: () => fetchReminder(reminderId),
  });

  const noteMutation = useMutation({
    mutationFn: (input: { body: string; uploadId?: string }) =>
      addReminderNote(reminderId, {
        body: input.body,
        ...(input.uploadId ? { uploadId: input.uploadId } : {}),
      }),
    onSuccess: () => {
      setNoteBody('');
      void queryClient.invalidateQueries({ queryKey: ['reminder', reminderId] });
      toast.success('Memo added');
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not add memo');
    },
  });

  const shareMutation = useMutation({
    mutationFn: (assigneeUserIds: string[]) =>
      updateReminder(reminderId, { assigneeUserIds }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['reminder', reminderId] });
      void queryClient.invalidateQueries({ queryKey: ['reminders'] });
      toast.success('Assignees updated');
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not update assignees');
    },
  });

  const usersQuery = useQuery({
    queryKey: ['org-users'],
    queryFn: listOrgUsers,
    enabled: canMutate,
  });

  if (detailQuery.isLoading) return <LoadingState rows={3} />;
  if (detailQuery.isError || !detailQuery.data) {
    return (
      <Card>
        <CardContent className="p-4 text-sm text-danger">Could not load task.</CardContent>
      </Card>
    );
  }

  const { reminder, notes } = detailQuery.data;
  const assigneeIds = new Set(reminder.assignees.map((a) => a.userId));

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="text-base">{reminder.title}</CardTitle>
          <p className="mt-1 text-xs text-fg-muted">
            Due {new Date(reminder.dueAt).toLocaleString()} · {reminder.status}
          </p>
        </div>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Close
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {reminder.description ? (
          <p className="whitespace-pre-wrap text-sm text-fg-secondary">{reminder.description}</p>
        ) : null}

        {canMutate ? (
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-fg-muted">Share with</Label>
            <select
              multiple
              className="min-h-[88px] w-full rounded-md border border-border bg-surface px-2 py-1 text-sm"
              value={[...assigneeIds]}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions).map((o) => o.value);
                shareMutation.mutate(selected);
              }}
            >
              {(usersQuery.data?.users ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} ({u.email})
                </option>
              ))}
            </select>
            <p className="text-xs text-fg-muted">Hold Ctrl/Cmd to select multiple people.</p>
          </div>
        ) : null}

        <div>
          <h3 className="mb-2 text-sm font-medium">Memo / follow-ups</h3>
          <ul className="space-y-3">
            {notes.length === 0 ? (
              <li className="text-sm text-fg-muted">No memos yet.</li>
            ) : (
              notes.map((note) => <NoteRow key={note.id} note={note} />)
            )}
          </ul>
        </div>

        {canMutate ? (
          <form
            className="space-y-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (!noteBody.trim()) return;
              noteMutation.mutate({ body: noteBody.trim() });
            }}
          >
            <Label htmlFor={`note-${reminderId}`}>Add memo</Label>
            <p className="text-xs text-fg-muted">
              Type a follow-up, or record a voice note — it will be transcribed and saved as a memo.
            </p>
            <VoiceRecorder
              autoStructure={false}
              uploadKind="reminder_voice_memo"
              onComplete={(result) => {
                const transcript = result.transcript.trim();
                if (!transcript) {
                  toast.error('No speech detected — try recording again.');
                  return;
                }
                noteMutation.mutate({
                  body: transcript,
                  uploadId: result.uploadId,
                });
              }}
            />
            <textarea
              id={`note-${reminderId}`}
              rows={3}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
              value={noteBody}
              onChange={(e) => setNoteBody(e.target.value)}
              placeholder="Followed up with Arrow regarding the LAA…"
            />
            <Button type="submit" size="sm" disabled={noteMutation.isPending || !noteBody.trim()}>
              Add memo
            </Button>
          </form>
        ) : null}
      </CardContent>
    </Card>
  );
}

function NoteRow({ note }: { note: ReminderNoteItem }) {
  return (
    <li className="rounded-md border border-border px-3 py-2">
      <div className="flex items-center justify-between gap-2 text-xs text-fg-muted">
        <span>{note.createdBy.name ?? 'Someone'}</span>
        <span>{new Date(note.createdAt).toLocaleString()}</span>
      </div>
      {note.uploadId ? <NoteVoicePlayer uploadId={note.uploadId} /> : null}
      <p className="mt-1 whitespace-pre-wrap text-sm text-fg-secondary">{note.body}</p>
    </li>
  );
}

function NoteVoicePlayer({ uploadId }: { uploadId: string }) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchUploadSignedUrl(uploadId)
      .then((signed) => {
        if (!cancelled) setUrl(signed.url);
      })
      .catch(() => {
        if (!cancelled) setUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [uploadId]);

  if (!url) {
    return <p className="mt-2 text-xs text-fg-muted">Voice memo</p>;
  }

  return (
    <div className="mt-2">
      {/* Transcript below serves as the textual alternate for this internal memo. */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio controls src={url} className="w-full" preload="metadata" />
    </div>
  );
}

function CreateTaskSheet({
  open,
  onOpenChange,
  buildingId,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  buildingId?: string;
  onCreated: (id: string) => void;
}) {
  const meQuery = useQuery({ queryKey: ['me'], queryFn: fetchMe });
  const buildingsQuery = useQuery({
    queryKey: ['buildings', 'tasks-create'],
    queryFn: () => listBuildings({ limit: 100 }),
    enabled: open && !buildingId,
  });
  const usersQuery = useQuery({
    queryKey: ['org-users'],
    queryFn: listOrgUsers,
    enabled: open,
  });

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [entityBuildingId, setEntityBuildingId] = useState(buildingId ?? '');
  const [assigneeIds, setAssigneeIds] = useState<string[]>([]);
  const [voiceUploadId, setVoiceUploadId] = useState<string | null>(null);
  const [voiceTranscript, setVoiceTranscript] = useState<string | null>(null);
  const [voiceLocationHint, setVoiceLocationHint] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setTitle('');
      setDescription('');
      setDueAt('');
      setEntityBuildingId(buildingId ?? '');
      setAssigneeIds([]);
      setVoiceUploadId(null);
      setVoiceTranscript(null);
      setVoiceLocationHint(null);
    }
  }, [open, buildingId]);

  const createMutation = useMutation({
    mutationFn: async () => {
      const me = meQuery.data?.user;
      if (!me?.orgId) throw new Error('Org not loaded');
      const due = dueAt
        ? new Date(`${dueAt}T09:00:00`).toISOString()
        : new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString();
      const linkedBuildingId = buildingId || entityBuildingId;
      const created = await createReminder({
        title: title.trim(),
        description: description.trim() || null,
        entityType: linkedBuildingId ? 'building' : 'org',
        entityId: linkedBuildingId || me.orgId,
        dueAt: due,
        priority: 'medium',
        assigneeUserIds: assigneeIds.length > 0 ? assigneeIds : me.id ? [me.id] : [],
      });

      // Persist the voice memo as the first dated note (transcript + audio).
      let voiceNoteSaved = true;
      if (voiceUploadId && voiceTranscript?.trim()) {
        try {
          await addReminderNote(created.reminder.id, {
            body: voiceTranscript.trim(),
            uploadId: voiceUploadId,
          });
        } catch {
          voiceNoteSaved = false;
        }
      }

      return { ...created, voiceNoteSaved };
    },
    onSuccess: (data) => {
      toast.success(
        data.voiceNoteSaved
          ? 'Task created'
          : 'Task created, but the voice memo could not be saved — add it from the task detail',
      );
      setTitle('');
      setDescription('');
      setDueAt('');
      setAssigneeIds([]);
      setVoiceUploadId(null);
      setVoiceTranscript(null);
      setVoiceLocationHint(null);
      onCreated(data.reminder.id);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not create task');
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    createMutation.mutate();
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full max-w-lg flex-col overflow-y-auto">
        <SheetHeader>
          <SheetTitle>New task</SheetTitle>
          <SheetDescription>
            Sticky-note style follow-up. Add memos over time and share with teammates.
          </SheetDescription>
        </SheetHeader>
        <form onSubmit={handleSubmit} className="mt-4 flex flex-1 flex-col gap-4">
          <div className="flex flex-col gap-1.5 rounded border border-dashed border-border p-3">
            <Label className="text-xs uppercase tracking-wide text-fg-muted">
              Voice memo (optional)
            </Label>
            <p className="text-xs text-fg-muted">
              Speak the task — including a building name or address when you know them —
              and SiteWise will fill in the subject, notes, and building. The audio is saved
              as the first memo.
            </p>
            <VoiceRecorder
              autoStructure
              uploadKind="reminder_voice_memo"
              {...(entityBuildingId || buildingId
                ? { buildingId: entityBuildingId || buildingId }
                : {})}
              onReset={() => {
                setVoiceUploadId(null);
                setVoiceTranscript(null);
                setVoiceLocationHint(null);
              }}
              onComplete={(result) => {
                setVoiceUploadId(result.uploadId);
                setVoiceTranscript(result.transcript || null);
                if (result.draft) {
                  const suggestedBuildingId = result.draft.buildingId ?? null;
                  const locationConfidence =
                    typeof result.draft.locationConfidence === 'number'
                      ? result.draft.locationConfidence
                      : null;
                  const locationConfidentEnough =
                    locationConfidence === null
                      ? Boolean(suggestedBuildingId)
                      : locationConfidence >= LOCATION_AUTO_SELECT_THRESHOLD;
                  const canApplyBuilding =
                    !buildingId &&
                    Boolean(suggestedBuildingId) &&
                    locationConfidentEnough;

                  if (
                    suggestedBuildingId &&
                    !canApplyBuilding &&
                    !buildingId &&
                    locationConfidence !== null &&
                    locationConfidence < LOCATION_AUTO_SELECT_THRESHOLD
                  ) {
                    const match = buildingsQuery.data?.buildings.find(
                      (b) => b.id === suggestedBuildingId,
                    );
                    setVoiceLocationHint(
                      match
                        ? `Suggested building: ${match.name} (${Math.round(locationConfidence * 100)}% confidence) — select it below if correct.`
                        : 'Voice suggested a building; pick it from the dropdown to confirm.',
                    );
                  } else {
                    setVoiceLocationHint(null);
                  }

                  setTitle((prev) => result.draft?.title ?? prev);
                  setDescription((prev) => result.draft?.description ?? prev);
                  if (canApplyBuilding && suggestedBuildingId) {
                    setEntityBuildingId(suggestedBuildingId);
                  }
                } else if (result.transcript.trim()) {
                  setDescription((prev) =>
                    prev.trim().length > 0 ? prev : result.transcript.trim(),
                  );
                }
              }}
            />
            {voiceLocationHint ? (
              <p className="text-xs text-fg-secondary">{voiceLocationHint}</p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="task-title">Subject</Label>
            <Input
              id="task-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="TCO follow-up"
              required
              maxLength={200}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="task-desc">Notes</Label>
            <textarea
              id="task-desc"
              rows={3}
              className="rounded-md border border-border bg-surface px-3 py-2 text-sm"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Initial context…"
            />
          </div>
          {!buildingId ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="task-building">Building (optional)</Label>
              <select
                id="task-building"
                className="h-10 rounded-md border border-border bg-surface px-3 text-sm"
                value={entityBuildingId}
                onChange={(e) => setEntityBuildingId(e.target.value)}
              >
                <option value="">Org-wide</option>
                {(buildingsQuery.data?.buildings ?? []).map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="task-due">Due date</Label>
            <Input
              id="task-due"
              type="date"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
            />
            <p className="text-xs text-fg-muted">Defaults to 3 days from now if left blank.</p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="task-assignees">Share with</Label>
            <select
              id="task-assignees"
              multiple
              className="min-h-[88px] rounded-md border border-border bg-surface px-2 py-1 text-sm"
              value={assigneeIds}
              onChange={(e) =>
                setAssigneeIds(Array.from(e.target.selectedOptions).map((o) => o.value))
              }
            >
              {(usersQuery.data?.users ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} ({u.email})
                </option>
              ))}
            </select>
          </div>
          <SheetFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              Create task
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
